/**
 * What a re-parse proposal rests on, drawn the same way wherever it is shown.
 *
 * The stored value and the engine's new reading each carry the document and the
 * place in it they were read from, a way straight to that place, and — for a
 * computed figure that has no document — the formula and the real numbers it
 * used. A proposal written before evidence was retained says so, rather than
 * presenting a generic engine label as a source. A value a PERSON decided is
 * shown with who, when and why.
 *
 * The Data Intelligence Review renders a re-parse as a choice between these
 * two blocks, one field at a time; there is no panel that answers a record's
 * proposals together.
 */
import { FileSearch, UserCheck } from 'lucide-react'

import type { ReparseEvidence, ReparsePriorDecision, SourceAnchor } from '@/services/ingestion'
import { label, show, sourceAnchor } from './reparseFormat'

export type OpenEvidence = (sha256: string, anchor: SourceAnchor | null) => void

function locatorText(evidence: ReparseEvidence): string | null {
  if (evidence.cellRef) return evidence.cellRef
  if (evidence.page != null) return `page ${evidence.page}`
  if (evidence.lineNo != null) return `line ${evidence.lineNo}`
  return null
}

/** A computed field (ltv, ltc, debt yield, ...) has no document to cite — it
 * is a ratio of other fields. Show the formula AND the real numbers it used,
 * so the result is verifiable instead of a bare percentage nobody can check. */
function CalculationBreakdown({ evidence }: { evidence: ReparseEvidence }) {
  const inputs = evidence.inputs ?? []
  return (
    <div className="text-[9px] leading-relaxed">
      <div className="text-stone-400">
        <span className="text-amber-200">Computed</span>
        {evidence.formula ? <> · {evidence.formula}</> : null}
      </div>
      {inputs.length > 0 ? (
        <div className="mt-1 space-y-0.5 rounded border border-stone-800 bg-black/30 px-1.5 py-1">
          {inputs.map((input) => (
            <div key={input.field} className="flex items-center justify-between gap-2">
              <span className="text-stone-500">{label(input.field)}</span>
              <span className="font-mono text-stone-300">{show(input.value, input.field)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-1 text-stone-500">
          The inputs behind this figure were not retained on this run — re-run the desk to
          regenerate them.
        </div>
      )}
    </div>
  )
}

export function EvidenceDetails({
  evidence,
  fallbackSource,
  sourceRole,
  onOpenSource,
}: {
  evidence?: ReparseEvidence | null
  fallbackSource?: string
  sourceRole: 'Stored source' | 'Proposal source'
  onOpenSource?: OpenEvidence
}) {
  if (!evidence) {
    return (
      <div className="mt-2 rounded border border-stone-800 bg-black/20 px-2 py-1.5 text-[9px] leading-relaxed text-stone-500">
        <span className="block font-medium uppercase tracking-wide text-stone-400">{sourceRole}</span>
        Source evidence was not retained on this legacy proposal. Re-run the desk to regenerate it with citations.
        {fallbackSource ? <span className="block font-mono text-stone-600">Recorded label: {fallbackSource}</span> : null}
      </div>
    )
  }

  const location = locatorText(evidence)
  const anchor = sourceAnchor(evidence)
  const canOpen = Boolean(
    onOpenSource
    && evidence.sourceSha256
    && anchor
    && (anchor.lineNo || anchor.page || anchor.cell || anchor.snippet),
  )
  const files = evidence.sourceFiles ?? []
  return (
    <div className="mt-2 space-y-1 rounded border border-stone-800 bg-black/25 px-2 py-1.5">
      <div className="text-[8px] font-medium uppercase tracking-wide text-stone-500">{sourceRole}</div>
      <div className="flex min-w-0 items-start gap-1.5">
        <FileSearch className={`mt-px h-3 w-3 shrink-0 ${
          evidence.availability === 'recorded' || evidence.availability === 'computed'
            ? 'text-amber-300' : 'text-stone-600'
        }`} />
        <div className="min-w-0 flex-1">
          {evidence.availability === 'recorded' ? (
            <div className="flex min-w-0 flex-wrap items-center gap-x-1 text-[9px]">
              {canOpen ? (
                <button
                  type="button"
                  onClick={() => onOpenSource?.(evidence.sourceSha256!, anchor)}
                  className="max-w-full break-all text-left text-amber-200 underline decoration-amber-400/30 underline-offset-2 hover:text-amber-100"
                  title={`Open ${evidence.source || 'the stored source'} at the exact cited value`}
                >
                  {evidence.source || 'Stored source'}
                </button>
              ) : (
                <span
                  className="max-w-full break-all text-stone-300"
                  title={evidence.sourceSha256 ? 'This citation has no exact field-level locator.' : 'The stored source file is unavailable.'}
                >
                  {evidence.source || 'Stored source'}
                </span>
              )}
              {location ? <span className="font-mono text-stone-500">· {location}</span> : null}
              {evidence.method ? (
                <span className="rounded bg-stone-800 px-1 py-px font-mono text-[8px] text-stone-500">
                  {evidence.method.replace(/_/g, ' ')}
                </span>
              ) : null}
            </div>
          ) : evidence.availability === 'computed' ? (
            <CalculationBreakdown evidence={evidence} />
          ) : (
            <div className="text-[9px] leading-relaxed text-stone-500">
              <span className={evidence.availability === 'not_found' ? 'text-amber-200' : 'text-stone-400'}>
                {evidence.availability === 'not_found' ? 'No supporting source found.' : 'Source provenance unavailable.'}
              </span>{' '}
              {evidence.detail}
            </div>
          )}
          {files.length > 0 ? (
            <div className="mt-0.5 truncate font-mono text-[8px] text-stone-600" title={files.join(', ')}>
              Files examined: {files.join(', ')}
            </div>
          ) : null}
        </div>
      </div>
      {evidence.raw ? (
        <blockquote className="break-words border-l border-amber-400/20 pl-2 text-[9px] italic leading-relaxed text-stone-400">
          “{evidence.raw}”
        </blockquote>
      ) : null}
      {evidence.normalization ? (
        <div className="font-mono text-[8px] leading-relaxed text-stone-600">
          Parsed as: {evidence.normalization}
        </div>
      ) : null}
    </div>
  )
}

export function PriorDecision({ decision }: { decision: ReparsePriorDecision }) {
  const at = decision.at ? new Date(decision.at) : null
  const when = at && !Number.isNaN(at.getTime()) ? at.toLocaleString() : null
  return (
    <div className="mt-2 flex items-start gap-1.5 rounded border border-amber-400/25 bg-amber-400/[0.06] px-2 py-1.5 text-[9px] leading-relaxed text-amber-100/80">
      <UserCheck className="mt-px h-3 w-3 shrink-0 text-amber-300" />
      <span>
        Stored value was decided by <strong className="font-medium text-amber-100">{decision.by || 'a reviewer'}</strong>
        {when ? ` on ${when}` : ''}.{decision.reason ? ` Reason: ${decision.reason}` : ''}
      </span>
    </div>
  )
}
